import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import LanguageSelector from './LanguageSelector';
import {
  LayoutDashboard, FileText, Search, CreditCard, Bell, LogOut, Menu, X,
  ChevronRight, Gauge, Users, Settings, Database, UserCog, PlusCircle, Clock,
  HardDrive, Cpu, ReceiptEuro, Car, AlertCircle, MessageSquare, Award,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Sidebar = () => {
  const { user, logout, isAdmin } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [version, setVersion] = useState('');
  const [lastUpdate, setLastUpdate] = useState('');
  const [madridTime, setMadridTime] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [businessHours, setBusinessHours] = useState(null);

  useEffect(() => {
    fetch(`${API}/version`).then(r => r.json()).then(d => { setVersion(d.version); setLastUpdate(d.last_update || ''); }).catch(() => {});
    fetch(`${API}/business-hours`).then(r => r.json()).then(d => setBusinessHours(d.hours)).catch(() => {});
  }, []);

  useEffect(() => {
    const DAY_KEYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const updateClock = () => {
      const now = new Date();
      const madrid = now.toLocaleString('es-ES', { timeZone: 'Europe/Madrid', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
      setMadridTime(madrid);
      const madridNow = new Date(now.toLocaleString('en-US', { timeZone: 'Europe/Madrid' }));
      const dayIdx = madridNow.getDay();
      const h = businessHours || {};
      // New format: per-day with shifts
      const dayKey = DAY_KEYS[dayIdx];
      const dayConfig = h[dayKey] || h.weekdays;
      if (dayConfig && dayConfig.enabled !== undefined) {
        // New per-day format
        if (!dayConfig.enabled) { setIsOpen(false); return; }
        const currentMins = madridNow.getHours() * 60 + madridNow.getMinutes();
        const open = (dayConfig.shifts || []).some(shift => {
          if (!shift.open || !shift.close) return false;
          const [oh, om] = shift.open.split(':').map(Number);
          const [ch, cm] = shift.close.split(':').map(Number);
          return currentMins >= oh * 60 + om && currentMins < ch * 60 + cm;
        });
        setIsOpen(open);
      } else if (dayConfig && dayConfig.open) {
        // Old weekdays/saturday/sunday format
        const currentMins = madridNow.getHours() * 60 + madridNow.getMinutes();
        const [oh, om] = dayConfig.open.split(':').map(Number);
        const [ch, cm] = dayConfig.close.split(':').map(Number);
        setIsOpen(currentMins >= oh * 60 + om && currentMins < ch * 60 + cm);
      } else {
        setIsOpen(false);
      }
    };
    updateClock();
    const interval = setInterval(updateClock, 1000);
    return () => clearInterval(interval);
  }, [businessHours]);

  const baseUserLinks = [
    { to: '/dashboard', icon: LayoutDashboard, label: t('Dashboard') },
    { to: '/tickets/new', icon: PlusCircle, label: t('Nueva Solicitud'), highlight: true },
    { to: '/tickets', icon: FileText, label: t('Mis Solicitudes') },
    { to: '/file-checker', icon: Search, label: t('Buscador Archivos'), beta: true },
    { to: '/ori-search', icon: HardDrive, label: t('Buscar ORI'), beta: true },
    { to: '/bosch-lookup', icon: Cpu, label: t('Bosch Translator'), beta: true },
    { to: '/vehicles', icon: Car, label: t('Base Vehiculos'), beta: true },
    { to: '/pcodes', icon: AlertCircle, label: t('Codigos DTC') },
    { to: '/credits', icon: CreditCard, label: t('Créditos') },
    { to: '/invoices', icon: ReceiptEuro, label: t('Facturas') },
    { to: '/notifications', icon: Bell, label: t('Notificaciones') },
    { to: '/profile', icon: UserCog, label: t('Mi Perfil') },
  ];

  // Admin menu: admin tools first, utilities below
  const adminUserLinks = [
    { to: '/file-checker', icon: Search, label: t('Buscador Archivos'), beta: true },
    { to: '/ori-search', icon: HardDrive, label: t('Buscar ORI'), beta: true },
    { to: '/bosch-lookup', icon: Cpu, label: t('Bosch Translator'), beta: true },
    { to: '/flex-jobs', icon: Gauge, label: t('FLEX Decrypt') },
    { to: '/vehicles', icon: Car, label: t('Base Vehiculos'), beta: true },
    { to: '/pcodes', icon: AlertCircle, label: t('Codigos DTC') },
    { to: '/notifications', icon: Bell, label: t('Notificaciones') },
    { to: '/profile', icon: UserCog, label: t('Mi Perfil') },
  ];

  const userLinks = isAdmin ? adminUserLinks : [
    ...baseUserLinks.slice(0, 6),
    ...(user?.features?.slave_manager ? [{ to: '/slave-manager', icon: Database, label: t('Gestor SLAVE') }] : []),
    ...(user?.features?.flex_decrypt ? [{ to: '/flex-jobs', icon: Gauge, label: t('FLEX Decrypt') }] : []),
    ...baseUserLinks.slice(6),
  ];

  const adminLinks = [
    { to: '/admin', icon: Gauge, label: t('Panel Admin') },
    { to: '/admin/tickets', icon: FileText, label: t('Solicitudes') },
    { to: '/admin/users', icon: Users, label: t('Usuarios') },
    { to: '/admin/invoices', icon: FileText, label: t('Facturación') },
    { to: '/admin/solutions', icon: Database, label: t('Soluciones Auto') },
    { to: '/admin/showcase', icon: Award, label: t('Trabajos Web') },
    { to: '/slave-manager', icon: Database, label: t('Gestor SLAVE') },
    { to: '/admin/services', icon: Settings, label: t('Servicios') },
    { to: '/admin/slave-tools', icon: Database, label: t('SLAVE Tools') },
    { to: '/admin/business-hours', icon: Clock, label: t('Horario Comercial') },
    { to: '/admin/settings', icon: Settings, label: t('Configuración') },
    { to: '/admin/sms-logs', icon: MessageSquare, label: t('SMS Logs') },
  ];

  const handleLogout = () => { logout(); navigate('/'); };

  const LinkItem = ({ to, icon: Icon, label, highlight, beta }) => (
    <NavLink to={to} onClick={() => setOpen(false)}
      className={({ isActive }) => `flex items-center gap-3 px-4 py-2.5 rounded-md transition-colors text-sm group ${highlight && !isActive ? 'bg-primary/10 text-primary border border-primary/20 hover:bg-primary/20' : isActive ? 'bg-primary/10 text-primary' : 'text-zinc-400 hover:text-white hover:bg-white/5'}`}>
      <Icon className="w-4 h-4" />
      <span className="flex-1">{label}</span>
      {beta && <span className="text-[9px] font-bold uppercase bg-amber-500/20 text-amber-400 border border-amber-500/30 px-1.5 py-0.5 rounded leading-none">Beta</span>}
      <ChevronRight className="w-3 h-3 opacity-0 group-hover:opacity-100" />
    </NavLink>
  );

  const content = (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b border-white/10">
        <div className="flex items-center gap-2">
          <img src="/ar-performance-logo-7.png" alt="AR" className="w-8 h-8 object-contain" onError={(e) => e.target.style.display='none'} />
          <div>
            <h2 className="font-heading text-sm font-bold text-white">AR PERFORMANCE</h2>
            <p className="text-[10px] text-zinc-500 tracking-widest">FILESERVICE</p>
          </div>
        </div>
      </div>

      <div className="p-4 border-b border-white/10">
        <p className="text-white text-sm font-medium truncate">{user?.company_name}</p>
        <p className="text-zinc-500 text-xs truncate">{user?.email}</p>
        {user && (
          <p className="text-xs mt-1">{t('Créditos')}: <span className="font-mono text-primary font-bold">{user.credits === -1 ? String.fromCharCode(8734) : user.credits}</span></p>
        )}
      </div>

      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {user?.is_admin && (
          <>
            <p className="text-[10px] text-zinc-600 uppercase tracking-wider px-4 mb-2">{t('Administración')}</p>
            {adminLinks.map(l => <LinkItem key={l.to} {...l} />)}
            <p className="text-[10px] text-zinc-600 uppercase tracking-wider px-4 mt-4 mb-2">{t('Herramientas')}</p>
          </>
        )}
        {!user?.is_admin && <p className="text-[10px] text-zinc-600 uppercase tracking-wider px-4 mb-2">{t('Principal')}</p>}
        {userLinks.map(l => <LinkItem key={l.to} {...l} />)}
      </nav>

      <div className="p-3 border-t border-white/10 space-y-2">
        <div className="flex items-center justify-between px-2">
          <div className="flex items-center gap-2 px-2 py-1.5 text-xs" data-testid="madrid-clock">
            <div className={`w-2 h-2 rounded-full flex-shrink-0 ${isOpen ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`} />
            <div className="flex-1">
              <p className={`font-mono font-medium ${isOpen ? 'text-green-400' : 'text-red-400'}`}>
                {madridTime} Madrid
              </p>
              <p className={`${isOpen ? 'text-green-500/70' : 'text-red-500/70'}`}>
                {isOpen ? t('EN LÍNEA') : t('CERRADO')}
              </p>
            </div>
          </div>
          <LanguageSelector compact />
        </div>
        <button onClick={handleLogout} className="flex items-center gap-3 px-4 py-2.5 w-full rounded-md text-zinc-500 hover:text-white hover:bg-white/5 transition-colors text-sm">
          <LogOut className="w-4 h-4" /><span>{t('Cerrar Sesión')}</span>
        </button>
        <button onClick={() => { caches.keys().then(names => names.forEach(n => caches.delete(n))); window.location.reload(true); }}
          className="flex items-center gap-3 px-4 py-1.5 w-full rounded-md text-zinc-600 hover:text-primary hover:bg-primary/5 transition-colors text-xs" data-testid="refresh-app">
          <ChevronRight className="w-3 h-3" /><span>{t('Actualizar App')}</span>
        </button>
        {version && (
          <div className="px-4 py-2 text-center">
            <span className="text-zinc-600 text-[10px] font-mono" data-testid="portal-version">v{version}</span>
            {lastUpdate && <span className="text-zinc-700 text-[9px] block">{(() => {
              const d = new Date(lastUpdate + 'T00:00:00');
              const now = new Date();
              const diff = Math.floor((now - d) / (1000 * 60 * 60 * 24));
              if (diff === 0) return t('Actualizado hoy');
              if (diff === 1) return t('Actualizado ayer');
              return `${t('Hace')} ${diff} ${t('días')}`;
            })()}</span>}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      <button onClick={() => setOpen(true)} className="lg:hidden fixed top-4 left-4 z-50 bg-surface p-2 rounded-md text-white">
        <Menu className="w-5 h-5" />
      </button>
      {open && <div className="lg:hidden fixed inset-0 z-40 bg-black/60" onClick={() => setOpen(false)} />}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-surface border-r border-white/10 transform transition-transform ${open ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}>
        {content}
      </aside>
    </>
  );
};

export default Sidebar;
